import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import { jaJP } from '@mui/x-data-grid';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from './../../contexts/AuthContext';
import { usePost } from './../../apis/ApiRequests';
import { CustomDataGrid } from '../../components/dataGrid/CustomDataGrid';
import CustomOutlinedInput from '../../components/input/CustomOutlinedInput';
import Header from '../../components/Header';
import Frame from '../../components/uiParts/Frame';
import TitleBar from '../../components/uiParts/TitleBar';
import CustomConfirm from '../../components/confirm/CustomConfirm';
import CustomSnackBar from '../../components/alert/CustomSnackBar';
import CustomSelect from '../../components/select/CustomSelect';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useGridApiRef } from '@mui/x-data-grid';

const BidPreRegister = () => {

	//===============================================
	// initialize
	//===============================================
	//-----------------------------------------------
    // initialize - セッション情報を取得
	//-----------------------------------------------
    const { authInfo }   = useAuth();
    const facilityCode   = authInfo.facilityCode;
    const facilityName   = authInfo.facilityName;
    const userId         = authInfo.userId;
    const userName       = authInfo.userName;
    const authority      = authInfo.authority;
    const traderCode     = authInfo.traderCode;
    const trader         = authInfo.trader;
    
    const search    = useLocation().search;
    const query     = new URLSearchParams(search);
    const bidGroupNo = query.get('bidGroupNo');
    
	//-----------------------------------------------
    // initialize - useRef
	//-----------------------------------------------
    const apiRef          = useGridApiRef();

	//-----------------------------------------------
    // initialize - useState
	//-----------------------------------------------
    const [ contentsHeight,        setContentsHeight        ] = useState(window.innerHeight -150);
    const [ dataGridHeight,        setDataGridHeight        ] = useState(window.innerHeight -450);
    const [ containerHeight,       setContainerHeight       ] = useState(window.innerHeight -100);
    const [ dataGridWidth,         setDataGridWidth         ] = useState(window.innerWidth -370);
    const [ searchCount,           setSearchCount           ] = useState(0);
    const [ requireMessage,        setRequireMessage        ] = useState('');
    const [ targetId,              setTargetId              ] = useState(0);
    const [ targetCell,            setTargetCell            ] = useState('traderCode');
    const [ prevRow,               setPrevRow               ] = useState({});
    const [ dataInputList,         setDataInputList         ] = useState([]);
    const [ groupSuryoRemain,      setGroupSuryoRemain      ] = useState('');
    const [ bidList,               setBidList               ] = useState([]);
    const [ bidPersonList,         setBidPersonList         ] = useState([]);
    const [ unitName,              setUnitName              ] = useState('Kg');
    const [initialized,            setInitialized           ] = useState(false);
    const [ openDialogTrader,      setOpenDialogTrader      ] = useState(false);
    const [ traderList,            setTraderList            ] = useState([]);

    const navigate = useNavigate();
    
    // BidGroupデータ
    const [ bidGroup, setBidGroup ] = useState({
        bidGroupNo    : '',
        entryNo       : '',
        entryDate     : '',
        bidName       : '',
        nisugata      : '',
        kikakuCode    : '',
        kikaku        : '',
        kgSuryo       : '',
        kgKubun       : '',
        suryo         : '',
        bidPersonCode : '',
        bidPerson     : '',
        groupStatus   : '',
    });

    // UI Message
    const [ messageState, setMessageState ] = useState({
        snackBarTrue            : false,
        snackBarFalse           : false,
        snackBarDelete           : false,
        require                 : false,
        confirm                 : false,
        confirm_register        : false,
        confirm_register_before : false,
        confirm_vote            : false,
        confirm_vote_release    : false,
        confirm_delete_row      : false,
    });
    
	//===============================================
	// API
	//===============================================
	//-----------------------------------------------
    // API - getData / BidGroup
	//-----------------------------------------------
    const { response : getData_res, loading : getData_Loading, post:getData_post } = usePost();
    const getData = async () => {
        const postData = {
            'className'    : 'mch.models.table.BidGroup',
            'methodName'   : 'getData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'bidGroupNo'   : bidGroupNo
        }
        await getData_post(postData);
    };
	//-----------------------------------------------
    // API - getDataInGroup / BidList
	//-----------------------------------------------
    const { response : getDataInGroup_res, loading : getDataInGroup_Loading, post:getDataInGroup_post } = usePost();
    const getDataInGroup = async () => {
        const postData = {
            'className'    : 'mch.models.table.BidList',
            'methodName'   : 'getDataInGroup',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'bidGroupNo'   : bidGroupNo
        }
        await getDataInGroup_post(postData);
    };
	//-----------------------------------------------
    // API - setData / BidHistory
	//-----------------------------------------------
    const { response : setData_res, post:setData_post } = usePost();
    const setData = async (targetData) => {
        const postData = {
            'className'     : 'mch.models.table.BidHistory',
            'methodName'    : 'setData',
            'facilityCode'  : facilityCode,
            'userId'        : userId,
            'activeFlag'    : targetData.activeFlag,
            'userId'        : targetData.userId,
            'bidGroupNo'    : targetData.bidGroupNo,
            'seq'           : targetData.seq,
            'traderCode'    : targetData.traderCode,
            'trader'        : targetData.trader,
            'entryUserId'   : targetData.entryUserId,
            'entryUser'     : targetData.entryUser,
            'ip'            : targetData.ip,
            'entrySuryo'    : targetData.entrySuryo ? targetData.entrySuryo : 0,
            'entryUnit'     : targetData.entryUnit,
            'bidSuryo'      : targetData.bidSuryo ? targetData.bidSuryo : 0,
            'tanka'         : targetData.tanka,
            'conditionCode' : targetData.conditionCode,
            'condition'     : targetData.condition,
            'bidStatus'     : targetData.bidStatus,
        }
        await setData_post(postData);
    };
	//-----------------------------------------------
    // API - updateBidGroup / BidGroup
	//-----------------------------------------------
    const { response : updateBidGroup_res, post:updateBidGroup_post } = usePost();
    const updateBidGroup = async (bidStatus) => {
        const postData = {
            'className'     : 'mch.models.table.BidGroup',
            'methodName'    : 'updateBidGroup',
            'facilityCode'  : facilityCode,
            'userId'        : userId,
            'bidGroupNo'    : bidGroupNo,
            'entryDate'     : bidGroup.entryDate,
            'bidPersonCode' : bidGroup.bidPersonCode,
            'bidPerson'     : bidGroup.bidPerson,
            'groupStatus'   : bidStatus
        }
        await updateBidGroup_post(postData);
    };
	//-----------------------------------------------
    // API - searchData / masta.BidPerson
	//-----------------------------------------------
    const { response : getDataAll_bidPerson_res, post : getDataAll_bidPerson_post } = usePost();
    const getDataAll_bidPerson = async () => {
        const postData = {
            'className'    : 'mch.models.masta.BidPerson',
            'methodName'   : 'searchData',
            'facilityCode' : facilityCode,
            'userId'       : userId
        }
        await getDataAll_bidPerson_post(postData);
    };
	//-----------------------------------------------
    // API - getData / masta.BidPerson
	//-----------------------------------------------
    const { response : getData_bidPerson_res, post : getData_bidPerson_post } = usePost();
    const getData_bidPerson = async (bidPersonCode) => {
        const postData = {
            'className'    : 'mch.models.masta.BidPerson',
            'methodName'   : 'getData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'code'         : bidPersonCode
        }
        await getData_bidPerson_post(postData);
    };
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / unloadList)
	//-----------------------------------------------
    useEffect(() => {
        if (updateBidGroup_res) {
            setSearchCount(searchCount+1);
        }
    }, [updateBidGroup_res]);
        //-----------------------------------------------
        // API - searchData / masta.Trader
        //-----------------------------------------------
        const { response : getDataAll_trader_res, post : getDataAll_trader_post } = usePost();
        const getDataAll_trader = async () => {
            const postData = {
                'className'    : 'mch.models.masta.Trader',
                'methodName'   : 'searchData',
                'facilityCode' : facilityCode,
                'userId'       : userId
            }
            await getDataAll_trader_post(postData);
        };
	//===============================================
	// useEffect
	//===============================================
	//-----------------------------------------------
    // useEffect - ロード時の設定
	//-----------------------------------------------
    useEffect(() => {

        setSearchCount(searchCount+1);
        getDataAll_trader();
        
        const handleResize = () => {
            setContentsHeight(window.innerHeight - 150);
            setDataGridHeight(window.innerHeight - 450);
            setContainerHeight(window.innerHeight - 100);
            setDataGridWidth(window.innerWidth - 370);
        }
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        }
    },[]);
	//-----------------------------------------------
    // useEffect - 検索処理
	//-----------------------------------------------
    useEffect(() => {
        getData();
        getDataInGroup();
        getDataAll_bidPerson();
    }, [searchCount]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / unloadList)
	//-----------------------------------------------
    useEffect(() => {
        if (getData_res) {
            setBidGroup({ ...getData_res });
        }
    }, [getData_res]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / unloadList)
	//-----------------------------------------------
    useEffect(() => {
        setBidList([]);
        if (getDataInGroup_res) {
            setBidList(getDataInGroup_res.Data );
            }
    }, [getDataInGroup_res]);
    //-----------------------------------------------
    // useEffect - APIレスポンス(searchData / masta_trader)
    //-----------------------------------------------
    useEffect(() => {
        setTraderList([]);
        if (getDataAll_trader_res) {
            setTraderList(getDataAll_trader_res.Data);
        }
    }, [getDataAll_trader_res]);
	//-----------------------------------------------
    // useEffect - ブランク行を初期追加
	//-----------------------------------------------
    useEffect(() => {
        if (getDataInGroup_res && !initialized) {
            //bidGroup の単位から設定
            const newRows = generateNewRows(1, getData_res.kgKubun);
            // 「残り数量」を数量(bidSuryo)にセット
            let maxSuryo = getData_res.kgSuryo;
            if (getData_res.kgKubun !== 1) {
                maxSuryo = getData_res.suryo;
            }
            newRows[0] = {...newRows[0],
                bidSuryo: maxSuryo,
            };
            setDataInputList(prevDataInputList => [...prevDataInputList, ...newRows])
            setInitialized(true);

        }
    }, [getDataInGroup_res, initialized]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getDataAll / masta_BidPerson)
	//-----------------------------------------------
    useEffect(() => {
        setBidPersonList([]);
        if (getDataAll_bidPerson_res) {
            setBidPersonList(getDataAll_bidPerson_res.Data);
        }
    }, [getDataAll_bidPerson_res]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / masta_BidPerson)
	//-----------------------------------------------
    useEffect(() => {
        if (getData_bidPerson_res) {
            setBidGroup(prevSearchData => ({
                ...prevSearchData,
                bidPersonCode: getData_bidPerson_res.code,
                bidPerson: getData_bidPerson_res.displayName,
            }));
        }
    }, [getData_bidPerson_res]);

	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / unloadList)
	//-----------------------------------------------
    useEffect(() => {
        let maxSuryo = bidGroup.kgKubun == 1 ? bidGroup.kgSuryo : bidGroup.suryo
        if (bidGroup.kgKubun === 0) {
            setUnitName('箱');
            maxSuryo = bidGroup.suryo;
        } else if (bidGroup.kgKubun === 2) {
            setUnitName('本');
            maxSuryo = bidGroup.suryo;
        } else if (bidGroup.kgKubun === 3) {
            setUnitName('匹');
            maxSuryo = bidGroup.suryo;
        } else if (bidGroup.kgKubun === 4) {
            setUnitName('袋');
            maxSuryo = bidGroup.suryo;
        } else {
            setUnitName('Kg');
            maxSuryo = bidGroup.kgSuryo;
        }
        setGroupSuryoRemain(maxSuryo);
        if (dataInputList) {
            let groupSuryoAll = maxSuryo;
            dataInputList.map((row) => {
                groupSuryoAll = Number(groupSuryoAll) - Number(row.bidSuryo);
            })
            let remainSuryo = Number(groupSuryoAll).toFixed(2);
            remainSuryo= -0.0 ? 0.0 : Number(groupSuryoAll).toFixed(2);
            setGroupSuryoRemain(remainSuryo);
        }
    }, [dataInputList, bidGroup]);
	//-----------------------------------------------
    // API - getData / masta.Trader
	//-----------------------------------------------
    const { response : getData_trader_res, post : getData_trader_post } = usePost()
    const getData_trader = async (code) => {
        const postData = {
            'className'    : 'mch.models.masta.Trader',
            'methodName'   : 'getData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'code'         : code
        }
        await getData_trader_post(postData);
    };
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / masta_trader)
	//-----------------------------------------------
    useEffect(() => {
        if (getData_trader_res) {
            setDataInputList((prevData) => {
                return prevData.map((row) => {
                    if (row.id === targetId) {
                        if (getData_trader_res.displayName) {
                            row.traderCode  = getData_trader_res.code;
                            row.trader      = getData_trader_res.displayName;
                        } else {
                            setRequireMessage('マスタに登録がありません');
                            setMessageInfo('require', true);
                            row.traderCode  = prevRow.traderCode;
                            row.trader      = prevRow.trader;
                        }
                        return row;
                    }
                    return row;
                });
            });
        }
    }, [getData_trader_res]);
	//===============================================
	// eventHandler
	//===============================================
	//-----------------------------------------------
    // eventHandler - 日付の変更
	//-----------------------------------------------
    const handleChange_entryDate = (value) => {
        setSearchCount(searchCount+1);
    }
	//-----------------------------------------------
    // eventHandler - セリ人の変更
	//-----------------------------------------------
    const handleChangexBidPerson = (value) => {
        if (value == 0) {
            setBidGroup(prevSearchData => ({
                ...prevSearchData,
                bidPersonCode: '',
                bidPerson: '',
            }));
        } else {
            getData_bidPerson(value);
        }
    };
	//-----------------------------------------------
    // eventHandler - 登録ボタン
	//-----------------------------------------------
    const handleClick_Register = async () => {
        const requireStatus = checkedRequire(); // 必須項目をチェックする
        if (requireStatus) {
            const updatePromises = dataInputList.map(async (inputListData) => {
                const postData = {
                    'activeFlag'    : 1,
                    'bidGroupNo'    : inputListData.bidGroupNo,
                    'traderCode'    : inputListData.traderCode,
                    'seq'           : inputListData.seq,
                    'entryUserId'   : inputListData.entryUserId,
                    'entryUser'     : inputListData.entryUser,
                    'trader'        : inputListData.trader,
                    'ip'            : inputListData.ip,
                    'entrySuryo'    : inputListData.bidSuryo,
                    'entryUnit'     : inputListData.entryUnit,
                    'bidSuryo'      : inputListData.bidSuryo,
                    'tanka'         : inputListData.tanka,
                    'conditionCode' : inputListData.conditionCode,
                    'condition'     : inputListData.condition,
                    'bidStatus'     : 1
                }
                await setData(postData);
            });
            // 後処理
            try {
                await Promise.all(updatePromises);
                updateBidGroup(9);
                setSearchCount(searchCount+1);
                setMessageInfo('confirm', false);
                setMessageInfo('confirm_register', false);
                setMessageInfo('snackBarTrue', false);
                navigate('/bidGroup');
            } catch (error) {
                setMessageInfo('snackBarFalse', true);
            }
        } else {
            setMessageInfo('confirm', false);
            setMessageInfo('confirm_register', false);
            setMessageInfo('require', true);
        }
    };
	//-----------------------------------------------
    // eventHandler - ブランク行を追加
	//-----------------------------------------------
    const handleClick_addRow = (mode) => {
        let newId = dataInputList.length > 0 ? Math.max(...dataInputList.map(r => r.id)) + 1 : 1;
        const newRows = generateNewRows(newId,bidGroup.kgKubun);
        setDataInputList(prevDataInputList => [...prevDataInputList, ...newRows])
    }
	//-----------------------------------------------
    // eventHandler - ブランク行を追加
	//-----------------------------------------------
    const generateNewRows = (startId,kgKubun) => {
        const rows = [];
        for (let i = 0; i < 1; i++) {
            rows.push({
                id: startId + i,
                facilityCode  : facilityCode,
                userId        : userId,
                activeFlag    : 1,
                userId        : userId,
                bidGroupNo    : bidGroupNo,
                traderCode    : '',
                trader        : '',
                entryUserId   : userId,
                entryUser     : userName,
                seq           : 1,
                ip            : '',
                entrySuryo    : 0,
                entryUnit     : kgKubun === 1 ? 2 : 4,
                tanka         : '',
                conditionCode : '',
                condition     : '',
                bidStatus     : 1,
                bidSuryo      : 0,
                kgKubun       : Number(kgKubun) ,
            })
        }
        return rows;
    }
	//-----------------------------------------------
    // eventHandler - 取り消しボタン押下
	//-----------------------------------------------
    const handleCancel = () => {
        updateBidGroup(0);
        navigate('/bidGroup');
    };
	//-----------------------------------------------
    // eventHandler - 仲買人ヘルプを非表示
	//-----------------------------------------------
    const handleClick_dialog_trader_close = () => {
        setOpenDialogTrader(false);
    };
	//===============================================
	// dataGrid
	//===============================================
    //-----------------------------------------------
    // dataGrid - 行の更新処理
    //-----------------------------------------------
    const handleProcessRowUpdate = (newRow, oldRow) => {
        let targetId = newRow.id;
        setTargetId(targetId);
        setDataInputList((prevData) => {
            return prevData.map((row) => {
                if (row.id === targetId) {
                    if (targetCell == 'traderCode') getData_trader(newRow.traderCode);
                    return newRow;
                }
                return row;
            });
        });
        return newRow;
    };
	//-----------------------------------------------
    // dataGrid - 入札明細の見出し
	//-----------------------------------------------
    const unitMapping = {
        1: 'Kg',
        0: '箱',
        2: '本',
        3: '匹',
        4: '袋',
        5: '本',
        6: '匹',
        7: '袋',
    };
    const columns = [
        { field: 'method', headerName: '漁法',    width: 120, columnSeparatorWidth: 1, sortable:false, },
        { field: 'senmei', headerName: '荷主',    width: 150, columnSeparatorWidth: 1, sortable:false, },
        { field: 'displayGyoshu', headerName: '品名',    width: 310, columnSeparatorWidth: 1, sortable:false, },
        { field: 'kgSuryo', headerName: 'Kg数', width: 80, columnSeparatorWidth: 1, sortable:false, type:'number'},
        { field: 'suryo', headerName: '数量', width: 55, columnSeparatorWidth: 1, sortable:false, type:'number'},
        {
            field: 'kgKubun',
            headerName: '単位',
            sortable:false,
            width: 55,
            renderCell: (params) => (
              <div>
                {unitMapping[params.value] || params.value}
              </div>
            ),
        },
        { field: 'displayBiko',    headerName: '備考',     width: 280, columnSeparatorWidth: 1, sortable:false, },
    ];
	//-----------------------------------------------
    // dataGrid - 入札状況一覧の見出し
	//-----------------------------------------------
    const columns_history = [
        { field: 'traderCode', headerName: 'コード', width: 190, columnSeparatorWidth: 1, sortable:false, headerAlign:'right', align:'right', editable:true, },
        { field: 'trader', headerName: '仲買人', width: 250, columnSeparatorWidth: 1, sortable:false, },       
        { field: 'tanka', headerName: '単価', width: 160, columnSeparatorWidth: 1, type:'number', sortable:false, editable:true},
        { field: 'bidSuryo', headerName: '数量', width: 160, columnSeparatorWidth: 1, type:'number', sortable:false, editable:true},
        {
            field: 'kgKubun',
            headerName: '単位',
            width: 120, sortable:false,
            renderCell: (params) => (
              <div>
                {unitMapping[params.value] || params.value}
              </div>
            ),
        },
    ];
    //-----------------------------------------------
    // dataGrid - テンキーのクリック
    //-----------------------------------------------
    const handleCellKeyDown = (event, params) => {
        setTargetId(event.id);
        setTargetCell(event.field);
    };
    //-----------------------------------------------
    // dataGrid - 行の選択
    //-----------------------------------------------
    const handleClick_dataInputList = (event) => {

        // 対象のセルはマスタヘルプを表示
        if (event.field == 'trader') setOpenDialogTrader(true);

        // 現在のフォーカスをセット
        setTargetId(event.id);
        setTargetCell(event.field);

        const row = dataInputList.find(row => row.id === event.id);
        if (row) setPrevRow(row);
    };
	//-----------------------------------------------
    // dataGrid - フォーカス制御
	//-----------------------------------------------
    useEffect(() => {
        // データが読み込まれていることを確認
        if (dataInputList.length > 0) {
            
            // フォーカスを右側のセルに移動する
            let i = targetId;
            let j = 'traderCode';
            if (targetId) {
                if (targetCell == 'traderCode') j = 'tanka';
                if (targetCell == 'trader') j = 'tanka';
                if (targetCell == 'tanka') j = 'bidSuryo';
                if (targetCell == 'bidSuryo') j = 'traderCode';
                apiRef.current.setCellFocus(i, j);
            }
        }
    }, [dataInputList]);
	//===============================================
	// Function
	//===============================================
    //-----------------------------------------------
    // Function - 仲買人マスタヘルプの行の選択
    //-----------------------------------------------
    const handleClick_dataGrid_trader = (code, displayName) => {
        setDataInputList((prevData) => {
              return prevData.map((row) => {
                  if (row.id === targetId) {
                      row.traderCode = code
                      row.trader     = displayName
                      return row
                  }
              return row;
            })
        });
        handleClick_dialog_trader_close();
    };
	//-----------------------------------------------
	// Function - 登録前に残数量をチェックする
	//-----------------------------------------------
    const handleClick_Register_before = () => {
        const requireStatus = checkedRequire(); // 必須項目をチェックする
        if (requireStatus) {
            const num = Number(groupSuryoRemain);
            if (num > 0) {
                setMessageInfo('confirm_register_before', true);
            } else {
                setMessageInfo('confirm_register', true);
            }
        } else {
            setMessageInfo('require', true);
        }
    };
	//-----------------------------------------------
	// Function - 必須項目をチェックする
	//-----------------------------------------------
    const checkedRequire = () => {
        const errorFields = [];
        
        if (!bidGroup.bidPersonCode) errorFields.push('「登録者」');

        if (dataInputList.some(row => !row.traderCode)) errorFields.push('「仲買人」');
        if (dataInputList.some(row => !row.tanka)) errorFields.push('「単価」');
        if (dataInputList.some(row => !row.bidSuryo)) errorFields.push('「数量」');
    
        if (errorFields.length) {
            const text = errorFields.join('') + 'が入力されていません';
            setRequireMessage(text);
            return false; 
        }
        if (!dataInputList.length) {
            setRequireMessage('落札対象が1件も登録されていません');
            return false;
        }
        return true;
    };
	//-----------------------------------------------
    // Function - 選択行の削除
	//-----------------------------------------------
    const delete_row = async () => {
        if(targetId){
            setDataInputList(prevList => prevList.filter(item => item.id !== targetId));
            setMessageInfo('confirm_delete_row', false)
            setMessageInfo('snackBarDelete', true)
            setTargetId(0);
        }else{
            setRequireMessage('行が選択されていません');
            setMessageInfo('confirm_delete_row', false)
            setMessageInfo('require', true);
        }
    };
	//===============================================
	// UI Message
	//===============================================
    const setMessageInfo = (target, state) => {
        setMessageState(prevSearchData => ({
            ...prevSearchData,
            [target]: state,
        }));
    };
    //===============================================
    // Css
    //===============================================
    const dialog_button_close = { 
        fontSize: '16px', height:'40px', width:'690px', color:'#fff', backgroundColor:'#808080'
    };
	//===============================================
	// JSXのレンダー
	//===============================================
    return (
        <>
            <Header loginInfo="on" backButton="on" backPage="/bidGroup" setEntryDate={handleChange_entryDate} />
            <div className='bidManager bodyContens'>
                <TitleBar title="確定売り" />
                <div style={{ display: 'flex', height: containerHeight }} className='mainContents'>
                    <div style={{ flex: 1, padding: '0px' }}>
                        <Frame classname="frame" title="入札情報">
                            <div style={{display:'flex'}}>
                                <div style={{padding:'8px', fontWeight:'bold', backgroundColor:'#305496', color:'#fff', width:'130px'}}><span>確定売り</span></div>
                                <div style={{marginLeft:'8px'}}>
                                    <CustomOutlinedInput
                                        type  = "text"
                                        id    = "bidManager_bidGroupNo"
                                        label = "入札No"
                                        value = { bidGroup.entryNo }
                                        width = "80px"
                                    />
                                </div>
                                <div style={{marginLeft:'10px'}}>
                                    <CustomOutlinedInput
                                        type   = "text"
                                        id     = "bidManager_bidName"
                                        label  = "品名"
                                        value  = { bidGroup.bidName }
                                        width = "280px"
                                    />
                                </div>
                                {
                                    bidGroup.kgKubun == 1 ?
                                    <div style={{marginLeft:'10px'}}>
                                        <CustomOutlinedInput
                                            type  = "text"
                                            id    = "bidManager_groupSuryo"
                                            label = "合計Kg数量"
                                            value = { bidGroup.kgSuryo }
                                            width = "100px"
                                        />
                                    </div>
                                    :
                                    <div style={{marginLeft:'8px'}}>
                                        <CustomOutlinedInput
                                            type  = "text"
                                            id    = "bidManager_groupDNum"
                                            label = "合計数量"
                                            value = { bidGroup.suryo }
                                            width = "90px"
                                        />
                                    </div>
                                }
                                <div style={{marginLeft:'10px'}}>
                                    <CustomOutlinedInput
                                        type   = "text"
                                        id     = "bidManager_kgKubun"
                                        label  = "入札単位"
                                        value={
                                            (() => {
                                                const division = [
                                                    { id: '0', name: '箱' },
                                                    { id: '1', name: 'Kg' },
                                                    { id: '2', name: '本' },
                                                    { id: '3', name: '匹' },
                                                    { id: '4', name: '袋' },
                                                ].find(item => String(item.id) === String(bidGroup.kgKubun));
                                                return division ? division.name : '';
                                            })()
                                        }
                                        width = "120px"
                                    />
                                </div>
                            </div>
                            <div style={{marginTop:'10px'}}>
                                <CustomDataGrid
                                    getRowClassName={(params) =>
                                        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                    }
                                    editMode                = "row"
                                    rows                    = { bidList }
                                    columns                 = { columns }
                                    sx                      = { {height:'150px',fontSize:14,borderColor: 'primary.light',} }
                                    density                 = "compact"
                                    localeText              = { jaJP.components.MuiDataGrid.defaultProps.localeText }
                                    showColumnRightBorder
                                    showCellRightBorder 
                                    disableColumnMenu          = {true}
                                    disableRowSelectionOnClick
                                    hideFooter
                                    loading                 = { getDataInGroup_Loading }
                                />
                            </div>
                        </Frame>
                        <Frame classname="frame" title="入札状況">
                        <div style={{display:'flex'}}>
                            <div style={{marginBottom:'8px', marginLeft:'0px', display: authority == 3 ? 'none' : 'block'}}>
                            </div>
                            <div style={{marginBottom:'8px', marginLeft:'20px'}}>
                                <CustomOutlinedInput
                                    type     = "text"
                                    id       = "bidManager_groupSuryoRemain"
                                    label    = "残り数量"
                                    value    = { groupSuryoRemain }
                                    onChange = {(e) => setGroupSuryoRemain(e.target.value)}
                                    width    = "100px"
                                />
                            </div>
                            <div style={{padding:'8px', fontWeight:'bold'}}>
                                <span>{unitName}</span>
                            </div>
                            <div style={{marginBottom:'8px', marginLeft:'10px'}}>
                                <CustomSelect
                                    id         = "userList_traderCode"
                                    label      = "登録者"
                                    width      = "180px"
                                    dataList   = {bidPersonList} 
                                    selectedId = {bidGroup.bidPersonCode} 
                                    onChange   = {(e) => handleChangexBidPerson(e.target.value)} 
                                />
                            </div>
                            <div style={{ marginLeft:'20px' }}>
                                <Button
                                    color     = "secondary"
                                    style     = {{color:'#fff', backgroundColor:'#305496' }}
                                    size      = "midium"
                                    variant   = "contained"
                                    startIcon = {<CheckIcon />}
                                    onClick   = {() => handleClick_Register_before() }
                                >
                                登  録
                                </Button>
                            </div>
                            <div style={{ marginLeft:'10px' }}>
                                <Button
                                    color     = "secondary"
                                    style     = {{color:'#fff', backgroundColor:'#ff0000' }}
                                    size      = "midium"
                                    variant   = "contained"
                                    startIcon = {<CloseIcon />}
                                    onClick   = { handleCancel }
                                >
                                取り消し
                                </Button>
                            </div>
                            <div style={{ marginLeft:'60px' }}>
                                <Button
                                    color     = "secondary"
                                    style     = {{color:'#fff'}}
                                    size      = "midium"
                                    variant   = "contained"
                                    startIcon = {<AddCircleIcon />}
                                    onClick   = {() => handleClick_addRow(1) }
                                >
                                行追加
                                </Button>
                            </div>
                            <div style={{marginLeft:'10px'}}>
                                <Button
                                    color     = "secondary"
                                    style     = {{color:'#fff', backgroundColor: 'grey'}}
                                    size      = "midium"
                                    variant   = "contained"
                                    startIcon = {<DeleteIcon />}
                                    onClick   = {() => setMessageInfo('confirm_delete_row', true) }
                                >
                                行削除
                                </Button>
                            </div>
                        </div>
                            <CustomDataGrid
                                apiRef = { apiRef }
                                getRowClassName={(params) =>
                                    params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                }
                                editMode                = "row"
                                rows                    = { dataInputList }
                                columns                 = { columns_history }
                                sx                      = { {height:dataGridHeight,fontSize:14,borderColor: 'primary.light',} }
                                density                 = "compact"
                                localeText              = { jaJP.components.MuiDataGrid.defaultProps.localeText }
                                showColumnRightBorder
                                showCellRightBorder
                                hideFooter
                                disableRowSelectionOnClick = {false}
                                disableColumnMenu          = {true}
                                onCellClick                = {(event) => handleClick_dataInputList(event)}
                                processRowUpdate           = {handleProcessRowUpdate}
                                onCellKeyDown              = {handleCellKeyDown}
                            />
                        </Frame>
                    </div>
                </div>
                <div className='dialog_traderList'>
                    <Dialog open={ openDialogTrader } onClose={ handleClick_dialog_trader_close } maxWidth='lg' >
                        <DialogTitle>
                            <TitleBar title="仲買人マスタ検索" />
                        </DialogTitle>
                        <DialogContent style={{border:'none'}} sx = {{width:'1020px', height:'650px'}}>
                            {traderList.map(item => (
                                <Button
                                    color   = "secondary"
                                    size    ="large"
                                    variant ="contained"
                                    style   = {{color:'#fff', width:'170px', margin:'10px', height:'70px'}}
                                    onClick = {() => handleClick_dataGrid_trader(item.code, item.displayName)}
                                >{item.displayName}
                                </Button>
                            ))}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                color     = "tertiary"
                                size      = "large"
                                variant   = "contained"
                                sx        = { dialog_button_close }
                                style = {{width:'500px', marginRight:'25%', marginBottom:'2%'}}
                                onClick   = {() => handleClick_dialog_trader_close()}
                                startIcon = {<CloseIcon />}
                            >{'閉じる'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
                <CustomSnackBar
                    open            = {messageState.snackBarTrue}
                    onClose         = {() => setMessageInfo('snackBarTrue', false)}
                    severity        = "success"
                    backgroundColor = "#2e8b57"
                    message         = "登録しました"
                />
                <CustomSnackBar
                    open            = {messageState.snackBarFalse}
                    onClose         = {() => setMessageInfo('snackBarFalse', false)}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = "登録に失敗しました"
                />
                <CustomSnackBar
                    open            = {messageState.snackBarDelete}
                    onClose         = {() => setMessageInfo('snackBarDelete', false)}
                    severity        = "success"
                    backgroundColor = "#2e8b57"
                    message         = "行を削除しました"
                />
                <CustomSnackBar
                    open            = {messageState.require}
                    onClose         = {() => setMessageInfo('require', false)}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = {requireMessage}
                />
                <CustomConfirm
                    open      = {messageState.confirm_register}
                    onClose   = {() => setMessageInfo('confirm_register', false)}
                    onClickNG = {() => setMessageInfo('confirm_register', false)}
                    onClickOK = {() => handleClick_Register()}
                    message   = "登録処理を実行します。よろしいですか？"
                />
                <CustomConfirm
                    open      = {messageState.confirm_register_before}
                    onClose   = {() => setMessageInfo('confirm_register_before', false)}
                    onClickNG = {() => setMessageInfo('confirm_register_before', false)}
                    onClickOK = {() => handleClick_Register()}
                    message   = "残り数量があります。このまま落札登録を行ってよろしいですか？"
                />
                <CustomConfirm
                    open      = {messageState.confirm_delete_row}
                    onClose   = {() => setMessageInfo('confirm_delete_row', false)}
                    onClickNG = {() => setMessageInfo('confirm_delete_row', false)}
                    onClickOK = {() => delete_row(0)}
                    message   = {'選択行を削除します。よろしいですか？'}
                />
            </div>
        </>
    )
}
export default BidPreRegister;